import Service, { service } from '@ember/service';
import { queryManager } from 'ember-apollo-client';
import { ERROR_NO_PRINTER_SELECTED } from 'my-phorest/services/electron-app';

import clientCoursesQuery from 'my-phorest/gql/queries/client-courses.graphql';
import clientTreatCardAccount from 'my-phorest/gql/queries/client-treat-card-account.graphql';
import purchaseReceiptQuery from 'my-phorest/gql/queries/purchase-receipt.graphql';
import purchaseReceiptWithSignatureQuery from 'my-phorest/gql/queries/purchase-receipt-with-signature.graphql';
import requestReceiptPrint from 'my-phorest/gql/mutations/request-receipt-print.graphql';

export default class PurchaseReceiptService extends Service {
  @service('browser/window') window;
  @service electronApp;
  @service errorHandler;
  @service printingDevices;
  @service purchase;
  @service receiptSettings;
  @service router;
  @service session;
  @service swingBridge;

  @queryManager apollo;

  async fetchReceiptData(purchaseId) {
    const promises = [
      this.apollo.query(
        {
          query: this.session.business.useFiskalTrust
            ? purchaseReceiptWithSignatureQuery
            : purchaseReceiptQuery,
          variables: {
            id: purchaseId,
          },
        },
        'purchase'
      ),
      this.receiptSettings.fetchReceiptSettings(),
    ];

    const [purchase, receiptSettings] = await Promise.all(promises);
    const extraDataPromises = [];
    let nextAppointment, client, clientCourses;

    if (purchase.clientId) {
      if (receiptSettings?.showFutureAppointmentsAndCourses) {
        extraDataPromises.push(
          this.fetchClientNextAppointment(purchase.clientId).then((result) => {
            nextAppointment = result;
            return result;
          })
        );
        extraDataPromises.push(
          this.fetchClientCourses(purchase.clientId).then((result) => {
            clientCourses = result;
            return result;
          })
        );
      }

      if (receiptSettings?.showTreatCardPoints) {
        extraDataPromises.push(
          this.fetchClientTreatCardAccount(purchase.clientId).then((result) => {
            client = result;
            return result;
          })
        );
      }
    }

    await Promise.all(extraDataPromises);

    return {
      purchase,
      receiptSettings,
      nextAppointment,
      client,
      clientCourses,
    };
  }

  async fetchClientCourses(clientId) {
    try {
      const clientCourses = await this.apollo.query(
        {
          query: clientCoursesQuery,
          variables: {
            filterBy: {
              clientId,
            },
          },
        },
        'clientCourses'
      );
      return clientCourses.edges?.map((e) => e.node);
    } catch (error) {
      this.errorHandler.handle(error, { showError: false });
    }
  }

  async fetchClientNextAppointment(clientId) {
    try {
      return this.purchase.fetchClientNextAppointmentTask.perform(clientId);
    } catch (error) {
      this.errorHandler.handle(error, { showError: false });
    }
  }

  async fetchClientTreatCardAccount(clientId) {
    try {
      return await this.apollo.query(
        {
          query: clientTreatCardAccount,
          variables: {
            id: clientId,
          },
        },
        'client'
      );
    } catch (error) {
      this.errorHandler.handle(error, { showError: false });
    }
  }

  /**
   * Prints the receipt in Electron locally or remotely.
   *
   * @param {string} purchaseId
   * @param {object} [options]
   * @param {string} [options.branchId]
   * @param {boolean} [options.isDuplicate=false]
   * @param {boolean} [options.forceLocalPrinting=false]
   * @param {boolean} [options.openCashDrawerRemotely=false]
   * @param {number} [options.numberOfCopies=1]
   * @param {string} [options.ccCopyMode]
   * @param {function} [options.successCb]
   * @param {function} [options.failureCb]
   * @returns {Promise<void|*|void>}
   */
  async printReceiptInElectron(purchaseId, options = {}) {
    if (!this.electronApp.isRunningInElectron) return;

    const {
      branchId,
      isDuplicate = false,
      forceLocalPrinting = false,
      numberOfCopies = 1,
      ccCopyMode,
      successCb,
      failureCb,
    } = options;

    try {
      await this.electronApp.verifyHasPrinterToPrintTo();
    } catch (error) {
      // Ignore the error if no printer is selected through the first time print selection dialog.
      if (error?.code === ERROR_NO_PRINTER_SELECTED) {
        failureCb?.(error);
      } else {
        throw error;
      }
    }

    if (!forceLocalPrinting) {
      const remoteTargetId = await this.electronApp.getRemotePrintingTarget();
      if (remoteTargetId) {
        return this.printReceiptRemotely(remoteTargetId, purchaseId, options);
      }
    }

    const accountId = this.session.getAccountIdByBranchId(branchId);
    let path;

    try {
      path = this.router.urlFor(
        'accounts.account.purchase.receipt',
        accountId,
        purchaseId
      );
    } catch (error) {
      console.warn(
        `Issue with generating receipt link. Params: accountId: "${accountId}", purchaseId: "${purchaseId}"`
      );
      return this.errorHandler.handle(error);
    }

    const { protocol, host } = this.window.location;
    const receiptURL = new URL(`${protocol}//${host}${path}`);
    receiptURL.searchParams.append('printView', 'true');

    if (isDuplicate) {
      receiptURL.searchParams.append('duplicate', 'true');
    }

    if (ccCopyMode) {
      receiptURL.searchParams.append('cc_copy_mode', ccCopyMode);
    }
    return this.electronApp.printReceipt(
      receiptURL.toString(),
      {
        successCb,
        failureCb,
      },
      {
        copies: numberOfCopies,
        waitForDataLoadedAttr: true,
      }
    );
  }

  /**
   * Prints the receipt on a remote (Electron) target.
   *
   * @param {string} remoteTargetId
   * @param {string} purchaseId
   * @param {object} [options]
   * @param {number} [options.numberOfCopies]
   * @param {boolean} [options.openCashDrawerRemotely=false]
   * @param {function} [options.successCb]
   * @param {function} [options.failureCb]
   * @returns {Promise<void>}
   */
  async printReceiptRemotely(
    remoteTargetId,
    purchaseId,
    {
      numberOfCopies,
      openCashDrawerRemotely = false,
      successCb,
      failureCb,
    } = {}
  ) {
    try {
      await this.apollo.mutate(
        {
          mutation: requestReceiptPrint,
          variables: {
            purchaseId,
            numberOfCopies,
            printingDeviceId: remoteTargetId,
            openCashDrawer: openCashDrawerRemotely,
          },
        },
        'requestReceiptPrint'
      );
      successCb?.();
      this.printingDevices.showPrintRequestSentSuccessNotificationTask.perform(
        remoteTargetId
      );
    } catch (err) {
      if (typeof failureCb === 'function') {
        failureCb(err);
      } else {
        this.errorHandler.handle(err);
      }
    }
  }
}
